import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import GeneralText from '../stories/general-components/GeneralText';
import { Paper } from '@mui/material';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { Canvas, useFrame } from '@react-three/fiber';
import { RoundedBox, Sphere } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import CustomAppBanner from '../websiteV3/components/bos/CustomAppBanner';
import chroma from 'chroma-js';

const WeatherWidget = ({ weatherData, isDarkMode, mainColor }) => {
  const { t } = useTranslation();
  const shadowColor = chroma(mainColor).darken(1.5).alpha(0.37).css();

  const getWeatherIcon = (weather) => {
    switch (weather) {
      case 'Clear':
        return (
          <img
            src="/assets/v2/img/weather-8.png"
            width={40}
            height={40}
            alt="meteo"
          />
        );
      case 'Clouds':
        return (
          <img
            src="/assets/v2/img/weather-8-2.png"
            width={40}
            height={40}
            alt="meteo"
          />
        );
      case 'Rain':
        return (
          <img
            src="/assets/v2/img/weather-5.png"
            width={40}
            height={40}
            alt="meteo"
          />
        );
      case 'Snow':
        return (
          <img
            src="/assets/v2/img/weather-6.png"
            width={40}
            height={40}
            alt="meteo"
          />
        );
      default:
        return (
          <img
            src="/assets/v2/img/weather-7.png"
            width={40}
            height={40}
            alt="meteo"
          />
        );
    }
  };

  if (!weatherData?.current) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        padding: '15px',
        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: '200px',
      }}
    >
      {weatherData?.current?.weather?.[0]?.main &&
        getWeatherIcon(weatherData?.current?.weather?.[0]?.main)}
      <div>
        <GeneralText
          text={weatherData?.current?.temp?.toFixed(0) + '°C'}
          fontSize="1.5rem"
          size="bold"
          primary={true}
        />
        <GeneralText
          text={t('weWaitWhe') + ' ' + weatherData?.current?.weather?.[0]?.description}
          fontSize="0.8rem"
          size="regular"
          primary={true}
        />
      </div>
    </Paper>
  );
};

const AuthLayout = ({ children, userName }) => {
  const { t } = useTranslation();
  const actualWeather = JSON.parse(localStorage.getItem('weatherData')) || {};
  const [weatherData, setWeatherData] = useState(actualWeather);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const colorStored = localStorage.getItem('mainColor');
  const secColorStored = localStorage.getItem('secColor');
  const [mainColor, setMainColor] = useState(colorStored || '#200EF0');
  const [secColor, setSecColor] = useState(secColorStored || '#200EF090');
  const apiKey = process.env.REACT_APP_WHEATHER_API_KEY;
  const navigate = useNavigate();
  const softwareVersion = process.env.REACT_APP_VERSION;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';

  const businessId = localStorage.getItem('businessId');
  const lottieRef = React.useRef(null);


  const shadowColor = chroma(mainColor || '#000000').darken(1.25).alpha(0.1).css();

  const MovingSphere = () => {
    const sphereRef = React.useRef();
    const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0, z: 0 });
    const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0, z: 0 });

    useEffect(() => {
      const updateTarget = () => {
        setTargetPosition({
          x: (Math.random() - 0.5) * 2,
          y: (Math.random() - 0.5) * 1.5,
          z: (Math.random() - 0.5) * 2
        });
        setTargetRotation({
          x: Math.random() * Math.PI * 2,
          y: Math.random() * Math.PI * 2,
          z: Math.random() * Math.PI * 2
        });
      };

      updateTarget(); // Set initial position
      const interval = setInterval(updateTarget, 5000);
      return () => clearInterval(interval);
    }, []);

    useFrame((state, delta) => {
      if (sphereRef.current) {
        // Smooth position interpolation with reduced calculations
        const pos = sphereRef.current.position;
        pos.x += (targetPosition.x - pos.x) * delta * 0.5;
        pos.y += (targetPosition.y - pos.y) * delta * 0.5;
        pos.z += (targetPosition.z - pos.z) * delta * 0.5;

        // Smooth rotation interpolation with reduced calculations
        const rot = sphereRef.current.rotation;
        rot.x += (targetRotation.x - rot.x) * delta * 0.3;
        rot.y += (targetRotation.y - rot.y) * delta * 0.3;
        rot.z += (targetRotation.z - rot.z) * delta * 0.3;

        // Simplified floating motion
        pos.y += Math.sin(state.clock.elapsedTime * 0.5) * 0.001;
      }
    });

    return (
      <Sphere
        ref={sphereRef}
        args={[0.8, 32, 32]}
        scale={[0.5, 0.5, 0.5]}
        position={[0, 0, 0]}
      >
        <meshStandardMaterial
          attach="material"
          color={mainColor}
          metalness={0.3}
          roughness={0.4}
          transparent
          opacity={0.8}
        />
      </Sphere>
    );
  };

  const MovingBox = () => {
    const boxRef = React.useRef();
    const [targetPosition, setTargetPosition] = useState({ x: 0, y: 0, z: 0 });
    const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0, z: 0 });

    useEffect(() => {
      const updateTarget = () => {
        setTargetPosition({
          x: (Math.random() - 0.5) * 3,
          y: (Math.random() - 0.5) * 2,
          z: (Math.random() - 0.5) * 3
        });
        setTargetRotation({
          x: Math.random() * Math.PI * 2,
          y: Math.random() * Math.PI * 2,
          z: Math.random() * Math.PI * 2
        });
      };

      updateTarget(); // Set initial position
      const interval = setInterval(updateTarget, 6000);
      return () => clearInterval(interval);
    }, []);

    useFrame((state, delta) => {
      if (boxRef.current) {
        // Smooth position interpolation with reduced calculations
        const pos = boxRef.current.position;
        pos.x += (targetPosition.x - pos.x) * delta * 0.4;
        pos.y += (targetPosition.y - pos.y) * delta * 0.4;
        pos.z += (targetPosition.z - pos.z) * delta * 0.4;

        // Smooth rotation interpolation with reduced calculations
        const rot = boxRef.current.rotation;
        rot.x += (targetRotation.x - rot.x) * delta * 0.2;
        rot.y += (targetRotation.y - rot.y) * delta * 0.2;
        rot.z += (targetRotation.z - rot.z) * delta * 0.2;

        // Simplified floating motion
        pos.y += Math.sin(state.clock.elapsedTime * 0.3) * 0.001;
      }
    });

    return (
      <RoundedBox
        ref={boxRef}
        args={[0.85, 0.85, 0.85]}
        radius={0.15}
        smoothness={4}
        position={[3, 2, -2]}
      >
        <meshStandardMaterial
          attach="material"
          color={secColor}
          emissive={secColor}
          emissiveIntensity={0.5}
          metalness={0.3}
          roughness={0.4}
          transparent
          opacity={0.8}
        />
      </RoundedBox>
    );
  };

  useEffect(() => {
    if (colorStored !== undefined && colorStored !== null) {
      setMainColor(colorStored || '#200EF0');
      setSecColor(secColorStored || '#200EF090');
    }

    if (businessId && businessId !== null) {
      const fetchData = async () => {
        try {
          const response = await nodeAxiosFirebase({
            t,
            noAuth: true,
            method: 'GET',
            url: `public/business?businessId=${businessId}`,
          });

          if (response?.data?.business?.mainColor !== undefined && response?.data?.business?.mainColor !== null) {
            setMainColor(response?.data?.business?.mainColor);
            setSecColor(response?.data?.business?.secColor);
            if (response?.data?.business?.mainColor !== undefined) {
              localStorage.setItem('mainColor', response?.data?.business?.mainColor);
              localStorage.setItem('secColor', response?.data?.business?.secColor);
            }
          }
        } catch (error) {
          console.error(error);
        }
      };
      fetchData();
    }
  }, [businessId]);

  useEffect(() => {
    const fetchData = async () => {
      if (latitude !== null && longitude !== null) {
        try {
          const storedWeatherData = localStorage.getItem('weatherData');
          if (storedWeatherData) {
            setWeatherData(JSON.parse(storedWeatherData));
            return;
          }

          const response = await fetch(
            `https://api.openweathermap.org/data/2.5/onecall?lat=${latitude}&lon=${longitude}&exclude=minutely&appid=${apiKey}&units=metric&lang=fr`
          );
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const data = await response.json();
          localStorage.setItem('weatherData', JSON.stringify(data));
          setWeatherData(data);
        } catch (error) {
          console.error('Error fetching weather:', error);
        }
      }
    };
    fetchData();
  }, [latitude, longitude, apiKey]);

  useEffect(() => {
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);
          },
          (error) => {
            console.error('Error getting location:', error);
          }
        );
      }
    };

    getLocation();
  }, []);


  useEffect(() => {
    return () => {
      if (lottieRef.current && lottieRef.current.destroy) {
        lottieRef.current.destroy();
      }
    };
  }, []);

  return (
    <div
      style={{
        backgroundColor: isDarkMode ? '#000000' : '#FFFFFF',
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100vh',
        width: '100vw',
        overflow: isMobile ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CustomAppBanner />

      {/* Weather Widget */}
      {!isMobile && weatherData?.current && (
        <WeatherWidget
          weatherData={weatherData}
          isDarkMode={isDarkMode}
          mainColor={mainColor}
        />
      )}

      {/* Background gradient effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at center, ${mainColor}16 0%, transparent 70%)`,
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />

      {/* 3D Canvas with performance optimizations */}
      <Canvas
        camera={{ position: [0, 2, 5], fov: 65 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        }}
        dpr={[1, 2]} // Limit pixel ratio for better performance
        performance={{ min: 0.5 }} // Allow frame rate to drop if needed
      >
        <ambientLight intensity={1.2} />
        <pointLight position={[5, 5, 5]} intensity={1.0} color="white" />
        <directionalLight position={[3, 3, 3]} intensity={0.8} color="white" />
        <spotLight position={[10, 10, 10]} intensity={1.5} angle={0.3} penumbra={0.5} color="white" />
        <MovingSphere />
        <MovingBox />
      </Canvas>

      {/* Main content container */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: '480px',
          padding: isMobile ? '20px' : '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Glassmorphism card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '30px',
            width: '100%',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: `0 8px 32px 0 ${shadowColor}`,
          }}
        >
          {children}
        </Paper>

        {/* Footer */}
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            padding: '20px',
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 3,
          }}
        >
          <div style={{ display: 'flex', gap: '20px' }}>
            <GeneralText
              text={t('backwebsite')}
              fontSize="0.8rem"
              size="regular"
              primary={true}
              classNameComponent="hover"
              onClick={() => navigate('/')}
            />
            <GeneralText
              text={t('privacyPolicy')}
              fontSize="0.8rem"
              size="regular"
              primary={true}
              classNameComponent="hover"
              onClick={() => navigate('/informations/mentions-legales')}
            />
            <GeneralText
              text={'Version ' + softwareVersion || ''}
              fontSize="0.8rem"
              size="regular"
              primary={true}
            />
          </div>
          <GeneralText
            text={'Intelligence Node Canada'}
            fontSize="0.8rem"
            size="regular"
            primary={true}
          />
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
